import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import dayjs from 'dayjs';
import _, { isEqual } from 'lodash';

import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useUsers from '../../context/UsersContext';
import useMenus from '../../context/MenuContext';
import masterDataService from '../../service/masterData.service';
import { FormHeaderUI, FormButtonPanelUI, DeleteDialogUI, FormBase } from '../Interface';
import { generateAutocompleteItems } from '../../utils/helper';
import OtherExpenseReports from './OtherExpenseReports';
import OtherExpenseTabs from './OtherExpenseTabs';
import OtherExpenseFields from './OtherExpenseFields';
import otherExpenseService from '../../service/otherExpense.service';
import useParam from '../../context/ParamContext';

export const OtherExpensePage = () => {
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [initValues, setInitValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [submenu, setSubmenu] = useState(null);
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [canEdit, setCanEdit] = useState(true);
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    project: [],
    company: [],
    costCenter: [],
    costAndRevenue: [],
    resourceOne: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const defaultOtherExpenseLine = {
    id: null,
    isNew: true,
    costAndRevenueTypeId: null,
    relatedId: null,
    name: '',
    amount: null,
    costCenterCode: '',
    projectId: null,
    companyId: null,
    reference: '',
  };

  const def = {
    date: dayjs(new Date()),
    projectId: null,
    costAndRevenueTypeId: null,
    costCenterCode: null,
    projectOrderNumber: '',
    otherExpenseLine: [],
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getMenus();
    getParam();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
    initDefaultValues();
  }, []);

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('otherExpense');
    setSubmenu(submenuId);
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .costCenterToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );
    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .costAndRevenueToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          company: resp[1],
          costCenter: resp[2],
          costAndRevenue: resp[3],
          resourceOne: resp[4],
        };

        setAutoCompleteData(aData);

        const acOptions = {
          project: generateAutocompleteItems(aData.project, ['projectName', 'orderNumber'], 'projectId'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          costCenter: generateAutocompleteItems(aData.costCenter, 'name', 'code'),
          costAndRevenue: generateAutocompleteItems(aData.costAndRevenue, 'name', 'id'),
          resourceOne: generateAutocompleteItems(aData.resourceOne, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const valuesToForm = () => {
    const { id, date, projectId, costAndRevenueTypeId, costCenterCode, otherExpenseLine } = selectedValues;

    setInitValues({
      id,
      date: dayjs(date),
      projectId,
      costAndRevenueTypeId,
      costCenterCode,
      projectOrderNumber: selectedValues.project?.orderNumber || '',
      otherExpenseLine: otherExpenseLine?.map((oE) => ({
        id: oE.id,
        costAndRevenueTypeId: oE.costAndRevenueTypeId,
        relatedId: oE.relatedId,
        name: oE.name,
        amount: oE.amount,
        costCenterCode: oE.costCenterCode,
        projectId: oE.projectId,
        companyId: oE.companyId,
        reference: oE.reference,
        createdBy: oE.createdBy,
        relatedMasterDataUrl: oE?.costAndRevenueType?.relatedMasterDataUrl ?? null,
      })),
    });
  };

  const validationSchema = Yup.object().shape({
    date: Yup.string().required(),
    projectId: Yup.string().nullable(),
    costAndRevenueTypeId: Yup.string().required(),
    costCenterCode: Yup.string().nullable(),
    projectOrderNumber: Yup.string(),
    otherExpenseLine: Yup.array().of(
      Yup.object().shape({
        costAndRevenueTypeId: Yup.string().required(),
        relatedId: Yup.string()
          .nullable()
          .when('relatedMasterDataUrl', {
            is: 'resourceOne',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema,
          }),

        name: Yup.string().nullable(),
        amount: Yup.number().required(),
        costCenterCode: Yup.string().nullable(),
        projectId: Yup.string().nullable(),
        companyId: Yup.string().nullable(),
        reference: Yup.string().nullable(),
      })
    ),
  });

  const insertObject = (values) => {
    const insertObject = {
      ...values,
      date: dayjs(values.date).startOf('day').add(12, 'hours').toISOString(),
      createdBy: user.userId,
    };

    otherExpenseService
      .createRow(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        setSelectedValues(data);
        setRowSelectionModel(data.id);
      })

      .finally(() => {
        //@TODO loader
      });
  };

  const updateObject = (values) => {
    const updData = {
      ...values,
      date: dayjs(values.date).startOf('day').add(12, 'hours').toISOString(),
      updatedBy: user.userId,
    };

    otherExpenseService.updateRow(updData).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      insertObject(values);
    }
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    otherExpenseService.deleteRow(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultSelectedValues);
    });
  };

  return (
    <>
      <FormBase
        name="OtherExpense"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setInitValues(defaultValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <OtherExpenseFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      autocompleteOptions={autocompleteOptions}
                      autoCompleteData={autoCompleteData}
                    />
                    <OtherExpenseTabs
                      setRefetchTable={setRefetchTable}
                      user={user}
                      disabled={viewField}
                      defaultField={defaultOtherExpenseLine}
                      autocompleteOptions={autocompleteOptions}
                      autoCompleteData={autoCompleteData}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);
                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <>
            <OtherExpenseReports
              refetch={{ refetchTable, setRefetchTable }}
              values={{ selectedValues, setSelectedValues }}
              viewField={viewField}
              operatorsDisabled={operatorsDisabled}
              autocompleteOptions={autocompleteOptions}
              rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            />
          </>
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
